import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ALERT_STATUS, type AlertStatus } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const Alert = z.object({
  id: z.string().uuid(),
  email: z.string(),
  criteria: z.record(z.unknown()),
  status: z.enum(ALERT_STATUS),
  last_notified_at: z.string().nullable(),
  created_at: z.string(),
  match_count: z.number(),
});
export type Alert = z.infer<typeof Alert>;
const AlertsResponse = z.object({ items: z.array(Alert) });

export function useAlerts() {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["alerts"],
    enabled: !!token,
    queryFn: () => apiFetch("/api/v1/admin/alerts", token!, AlertsResponse),
  });
}

const PatchAck = z.object({ id: z.string(), status: z.enum(ALERT_STATUS) });

export function usePatchAlert() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AlertStatus }) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/alerts/${id}`, token, PatchAck, { method: "PATCH", body: { status } });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["alerts"] }); },
  });
}
